import { Badge, Box, Button, CircularProgress, ClickAwayListener, Dialog, Divider, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popover, Popper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Ballot, CallEnd, ChatBubble, Checklist, Fullscreen, FullscreenExit, Group, ManageAccounts, MoreVert } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import MinimizedVideoSettingsBar from './MinimizedVideoSettingsBar.jsx'
import MinimizedAudioSettingsBar from './MinimizedAudioSettingsBar.jsx'
import { useToggleButtonsData } from '../../hooks/useToggleButtonsData'
import { useSocketsContext } from '../../contexts/sockets.context'
import { Icons, isMobileDevice } from '../../utils/utilities'
import { useMeetingStore } from '../../store/meeting.store'
import OnlineMeetingTimer from './OnlineMeetingTimer.jsx'
import MeetingSettings from './MeetingSettings.jsx'
import ReactionsBar from './ActionsBar/ReactionsBar.jsx'
import MeetingReactions from './ActionsBar/Reactions.jsx'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context.js'
import { useVirtualboardStore } from '../../store/virtualboard.store.jsx'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store.js'


const ActionsBar = observer( ( { meetingContainer, openMeetingInfoDialog, setIsManagingPoll } ) => {

    const [showMinimizedVideoSettings, setShowMinimizedVideoSettings] = useState( false )
    const [showMinimizedAudioSettings, setShowMinimizedAudioSettings] = useState( false )
    const [leaveOrEndWindowMode, setLeaveOrEndWindowMode] = useState( "options" )
    const [reactionBarAnchorlEl, setReactionBarAnchorlEl] = useState( false )
    const [showSettingsDialog, setShowSettingsDialog] = useState( false )
    const [showLeaveOptions, setShowLeaveOptions] = useState( false )
    const [showShareOptions, setShowShareOptions] = useState( false )
    const [showMoreOptions, setShowMoreOptions] = useState( false )
    const [endingSession, setEndingSession] = useState( false )
    const [leaving, setLeaving] = useState( false )

    const { border, palette, layout, breakpoints } = useTheme()
    const { meeting_id } = useParams()

    const smallWidth = useMediaQuery( breakpoints.down( 'sm' ) )
    const tinyWidth = useMediaQuery( '(max-width:400px)' )

    // const { onEvent, emitEvent } = useSocketsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const { turnOnCamera, turnOnMicrophone, publishVideo, publishAudio, leaveChannel, stopScreenShare, toggleScreenShare, replaceSharingTrack, toggleScreenRecording } = useMeetingActionsContext()

    const VirtualboardStore = useVirtualboardStore()
    const MeetingStore = useMeetingStore()

    const videoActionBtnGrpRef = useRef()
    const audioActionBtnGrpRef = useRef()
    const toggleShareButtonRef = useRef()
    const actionBarRef = useRef()

    const { raiseHandButtonDetails, recordButtonDetails, cameraButtonDetails, micButtonDetails, shareButtonDetails } = useToggleButtonsData()


    // LAYOUT
    const openParticipantsList = () => {
        const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'participants' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
        MeetingStore.setMeetingRoomLayoutSettings( {
            leftSideCollapseTab: 'participants',
            isLeftSideCollapseOpen: openFlag
        } )
    }

    const openParticipantsSettings = () => {
        const openFlag = !['meeting settings', 'webinar settings', 'class settings'].includes( MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab )
        MeetingStore.setMeetingRoomLayoutSettings( {
            leftSideCollapseTab: `${MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type} settings`,
            isLeftSideCollapseOpen: openFlag
        } )
    }

    const openChatsList = () => {
        const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'chats' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
        MeetingStore.setMeetingRoomLayoutSettings( {
            leftSideCollapseTab: 'chats',
            isLeftSideCollapseOpen: openFlag
        } )
        const newIndicatorsState = { ...MeetingStore.getMeetingChatsDetails.indicators }
        newIndicatorsState.chatbutton = false

        MeetingStore.setMeetingChatsDetails( {
            indicators: newIndicatorsState
        } )
    }

    const openMinimizedPoll = () => {
        MeetingStore.setMeetingRoomLayoutSettings( { isPollDialogMinimized: false } )
    }

    const openBackgroundSettings = async () => {
        const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'video Settings' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
        const processor = MeetingStore.getVideoProcessor
        if ( processor )
            await processor.enable()
        MeetingStore.setMeetingRoomLayoutSettings( {
            leftSideCollapseTab: 'video Settings',
            isLeftSideCollapseOpen: openFlag
        } )
    }

    const openPresentationManager = async () => {
        MeetingStore.setMeetingRoomLayoutSettings( { isPrentationsManagerOpen: true } )
    }

    const openVideoSettings = () => {
        setShowSettingsDialog( "video" )
    }
    const openAudioSettings = () => {
        setShowSettingsDialog( "audio" )
    }
    // ACTIONS
    const raiseHand = useCallback( async () => {

        if ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" ) return

        if ( MeetingStore.getMeetingState.raisedHand ) {
            const { success, message } = await OnlineSessionSocketStore.emit( "handraises:lower", { room_id: meeting_id } )
            if ( success )
                MeetingStore.setMeetingState( { raisedHand: false } )
            if ( message )
                toast( message )
            return
        }

        const { success, message } = await OnlineSessionSocketStore.emit( "handraises:raise", { room_id: meeting_id } )
        if ( success )
            MeetingStore.setMeetingState( { raisedHand: true } )
        if ( message )
            toast( message )

    }, [MeetingStore, OnlineSessionSocketStore, meeting_id] )

    const handleToggleScreeShareButtonClick = () => {
        const { isSharing } = MeetingStore.getMeetingState
        if ( isSharing ) {
            setShowShareOptions( true )
        } else {
            if ( MeetingStore.getCurrentMeeting.meeting_type !== 'discussion' || MeetingStore.getLocalUser.meeting_role === 'meeting-host' )
                setShowShareOptions( true )
            else
                toggleScreenShare()
        }
    }

    const startSharingScreen = async () => {
        await OnlineSessionSocketStore.emit( 'virtualboard:stop', { room_id: meeting_id } )
        console.log( "Start Screen Share 2" )
        MeetingStore.setMeetingState( { presentingFileId: null, presentingVirtualBoard: false, activePresenter: null } )
        MeetingStore.setPresentingDocument( null )
        await toggleScreenShare( true )
        setShowShareOptions( false )
    }

    const stopSharingVirtualBoard = async () => {
        const { success, message } = await OnlineSessionSocketStore.emit( "virtualboard:stop", { room_id: meeting_id } )
        MeetingStore.setMeetingState( { activePresenter: null, presentingVirtualBoard: false, presentingFileId: null } )
    }

    const resumePresenting = async () => {
        try {

            const { message } = await OnlineSessionSocketStore.emit( 'meeting:resume-presenting', { room_id: meeting_id } )
            if ( message )
                toast( message )

        } catch ( err ) {
            console.log( err )
        }
    }

    const handleLeaveChannel = async ( event ) => {
        setLeaving( true )
        const localUserId = MeetingStore.getLocalUser.user_id
        if ( MeetingStore.getCurrentMeeting.hosts.find( h => h.user_id === localUserId ) && MeetingStore.getRemoteUsers?.length > 0 ) {
            setShowLeaveOptions( true )
        } else {
            setLeaveOrEndWindowMode( "options" )
            await leaveChannel()
        }
        setLeaving( false )
    }

    const endMeeting = async ( confirm = false ) => {
        try {
            setEndingSession( true )
            if ( !confirm ) {
                const diff = ( new Date( MeetingStore.getCurrentMeeting.ends_at ) - new Date() ) / 1000

                if ( diff > 300 )
                    return setLeaveOrEndWindowMode( "confirmation" )
            }
            const { message } = await OnlineSessionSocketStore.emit( "meeting:end", { room_id: meeting_id } )
            await leaveChannel( "meeting-ended" )
            if ( message )
                toast( message )
            setLeaveOrEndWindowMode( "options" )
        } catch ( err ) {
            console.error( err )
        } finally {
            setEndingSession( false )
        }
    }

    useEffect( () => {
        const updateMeetingSettings = ( { newSettings } ) => {
            MeetingStore.setMeetingSettings( newSettings )

            if ( !newSettings.enable_screen_sharing && MeetingStore.getMeetingState.isSharing && MeetingStore.getLocalUser.meeting_role !== "meeting-host" ) {
                stopScreenShare( false )
            }
            if ( !newSettings.enable_microphone && MeetingStore.getMeetingState.isAudioOn && MeetingStore.getLocalUser.meeting_role !== "meeting-host" ) {
                publishAudio( false )
            }
            if ( !newSettings.enable_video_feed && MeetingStore.getMeetingState.isVideoOn && MeetingStore.getLocalUser.meeting_role !== "meeting-host" ) {
                publishVideo( false )
            }
        }

        const handleUserPermissionsUpdate = ( { newValue } ) => {
            MeetingStore.setUsersPermissions( newValue )
            if ( MeetingStore.getLocalUser.meeting_role !== "meeting-host" ) {
                if ( !newValue[MeetingStore.getLocalUser.user_id]?.screenSharing && MeetingStore.getMeetingState.isSharing ) {
                    stopScreenShare( false )
                }
                if ( !newValue[MeetingStore.getLocalUser.user_id]?.mic && MeetingStore.getMeetingState.isAudioOn ) {
                    publishAudio( false )
                }
                if ( !newValue[MeetingStore.getLocalUser.user_id]?.camera && MeetingStore.getMeetingState.isVideoOn ) {
                    publishVideo( false )
                }
            }
        }

        const handleTabSwitch = async () => {
            await leaveChannel( "tab-switch" )
        }

        const handeFullscreenChange = ( event ) => {
            const state = Boolean( document.fullscreenElement )
            MeetingStore.setMeetingRoomLayoutSettings( { isInFullscreen: state, fullScreenElement: state ? event.target : null } )
        }

        const handleNewPresentation = async ( { presentation_id, presenter, presentation_document, elements } ) => {
            const { isSharing } = MeetingStore.getMeetingState
            if ( isSharing )
                await toggleScreenShare()

            MeetingStore.setMeetingState( { presentingVirtualBoard: true, activePresenter: presenter } )

            VirtualboardStore.setBoardState( {
                currentSlide: presentation_document.currentSlide,
                slides: presentation_document.slides,
                document: presentation_document,
            } )
            VirtualboardStore.setElements( elements )

            MeetingStore.setPresentingDocument( presentation_document )
            // MeetingStore.setPresentingDocumentData( previous_state )
        }

        const handleVirtualboardStop = () => {
            MeetingStore.setMeetingState( { presentingVirtualBoard: false, activePresenter: null, presentingFileId: null } )
            MeetingStore.setPresentingDocument( null )
            VirtualboardStore.setElements( {} )
            // MeetingStore.setPresentingDocumentData( { events: {}, elements: {} } )
        }


        document.addEventListener( 'fullscreenchange', handeFullscreenChange )



        const unsubNewPresentationEvent = OnlineSessionSocketStore.on( 'presentation:new-presentation', handleNewPresentation )
        const unsubUserPermissionsUpdateEvent = OnlineSessionSocketStore.on( 'meeting:users-permission-updated', handleUserPermissionsUpdate )
        const unsubSessionSettingsUpdateEvent = OnlineSessionSocketStore.on( 'meeting:settings-updated', updateMeetingSettings )
        const unsubLeaveToNewTabEvent = OnlineSessionSocketStore.on( 'meeting:leave-to-newtab', handleTabSwitch )
        const unsubMeetingEndEvent = OnlineSessionSocketStore.on( 'meeting:ended', handleTabSwitch )
        const ubsubVbStopEvent = OnlineSessionSocketStore.on( "virtualboard:stoped", handleVirtualboardStop )
        return () => {
            unsubNewPresentationEvent()
            unsubUserPermissionsUpdateEvent()
            unsubSessionSettingsUpdateEvent()
            unsubLeaveToNewTabEvent()
            unsubMeetingEndEvent()
            ubsubVbStopEvent()
            document.removeEventListener( 'fullscreenchange', handeFullscreenChange )
        }
    }, [meeting_id, MeetingStore, publishAudio, publishVideo, stopScreenShare, leaveChannel, toggleScreenShare, OnlineSessionSocketStore, VirtualboardStore] )



    useEffect( () => {

        const handleKeyDown = ( event ) => {
            const { code, shiftKey, ctrlKey, altKey } = event

            if ( code === 'KeyD' && ctrlKey ) {
                event.preventDefault()
                turnOnMicrophone()
            } else if ( code === 'KeyB' && ctrlKey ) {
                event.preventDefault()
                MeetingStore.setMeetingRoomLayoutSettings( { isLeftSideCollapseOpen: !MeetingStore.getMeetingRoomLayoutSettings.isLeftSideCollapseOpen } )
            } else if ( code === 'KeyE' && ctrlKey ) {
                event.preventDefault()
                turnOnCamera()
            } else if ( code === 'KeyH' && altKey && MeetingStore.getMeetingState.isJoined ) {
                event.preventDefault()
                raiseHand()
            } else if ( code === 'KeyC' && altKey && ctrlKey && MeetingStore.getMeetingState.isJoined ) {
                if ( MeetingStore.getCurrentMeeting.meeting_type === 'hybrid-class' ) return
                event.preventDefault()
                const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'chats' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
                MeetingStore.setMeetingRoomLayoutSettings( {
                    leftSideCollapseTab: 'chats',
                    isLeftSideCollapseOpen: openFlag
                } )
                const newIndicatorsState = { ...MeetingStore.getMeetingChatsDetails.indicators }
                newIndicatorsState.chatbutton = false

                MeetingStore.setMeetingChatsDetails( {
                    indicators: newIndicatorsState
                } )
            } else if ( code === 'KeyP' && altKey && ctrlKey && MeetingStore.getMeetingState.isJoined ) {
                event.preventDefault()
                const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'participants' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
                MeetingStore.setMeetingRoomLayoutSettings( {
                    leftSideCollapseTab: 'participants',
                    isLeftSideCollapseOpen: openFlag
                } )
            } else if ( code === 'KeyL' && ctrlKey && MeetingStore.getMeetingState.isJoined ) {
                event.preventDefault()
                leaveChannel()
            } else if ( code === 'Slash' && ctrlKey && MeetingStore.getMeetingState.isJoined ) {
                event.preventDefault()
                setShowSettingsDialog( "general" )
            } else if ( code === 'KeyF' && ctrlKey && MeetingStore.getMeetingState.isJoined ) {
                event.preventDefault()
                MeetingStore.toggleFullScreen( meetingContainer.current )
            }
        }

        const container = meetingContainer.current
        if ( container ) {
            container?.addEventListener( 'keydown', handleKeyDown )
            return () => container?.removeEventListener( 'keydown', handleKeyDown )
        }

    }, [MeetingStore, turnOnCamera, turnOnMicrophone, raiseHand, meetingContainer, leaveChannel] )

    return (
        <Box ref={actionBarRef} id="actions" display="grid" gridTemplateColumns={{ md: "1fr 1fr 1fr", xs: "auto auto" }} gridTemplateAreas={{ md: `". pa sa"`, xs: `"pa sa"` }} alignItems="center" gap="10px" padding={{ md: "5px 20px", xs: "5px" }}>

            {Boolean( showSettingsDialog ) && <MeetingSettings open={Boolean( showSettingsDialog )} activeTab={showSettingsDialog} setOpen={() => setShowSettingsDialog( false )} container={actionBarRef} />}
            {MeetingStore.getCurrentMeetingSettings.enable_reactions && <ReactionsBar anchorEl={reactionBarAnchorlEl} open={Boolean( reactionBarAnchorlEl )} handleClose={() => setReactionBarAnchorlEl( null )} />}
            {MeetingStore.getCurrentMeetingSettings.enable_reactions && <MeetingReactions />}

            <Dialog sx={{ zIndex: 6000 }} slotProps={{ paper: { sx: { width: "auto" } } }} container={meetingContainer.current} onClose={() => {
                setShowLeaveOptions( false )
                setLeaveOrEndWindowMode( "options" )
            }} open={showLeaveOptions}>
                {leaveOrEndWindowMode === "options" && <Box padding="20px">
                    <Typography variant="h6" component="p" fontSize="16px" >End the {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type} or just leave?</Typography>
                    <Typography marginTop="20px" fontSize="14px">You can just leave if you don't want to end the call for everyone.</Typography>
                    <Box marginTop="20px" gap="10px" display="flex" alignItems="center" justifyContent="flex-end">
                        <Button loading={leaving} onClick={() => leaveChannel()} variant='outlined' >leave {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type}</Button>
                        <Button onClick={() => endMeeting()} color="error" variant='outlined' >End {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type}</Button>
                    </Box>
                </Box>}
                {leaveOrEndWindowMode === "confirmation" && <Box padding="20px">
                    <Typography variant="h6" component="p" fontSize="16px" >Are you sure?</Typography>
                    <Typography marginTop="20px" fontSize="14px">It’s too early, the scheduled end time hasn’t arrived yet.</Typography>
                    <Box marginTop="20px" gap="10px" display="flex" alignItems="center" justifyContent="flex-end">
                        <Button onClick={() => {
                            setShowLeaveOptions( false )
                            setLeaveOrEndWindowMode( "options" )
                        }} variant='outlined' disabled={endingSession} >No, Stay</Button>
                        <Button disabled={endingSession} startIcon={endingSession && <CircularProgress size={14} />} onClick={() => endMeeting( true )} color="error" variant='outlined' >Yes, End {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type} now</Button>
                    </Box>
                </Box>}
            </Dialog>

            <Popper placement='bottom-start' sx={{ zIndex: 2000 }} container={actionBarRef.current} anchorEl={toggleShareButtonRef.current} open={showShareOptions}>
                <ClickAwayListener onClickAway={() => setShowShareOptions( false )}>
                    <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: "10px", minWidth: "250px", marginBottom: "20px", bgcolor: palette.contentBg }}>
                        {MeetingStore.getMeetingState.isSharing && <MenuList onClick={() => setShowShareOptions( false )}>
                            {MeetingStore.getLocalUser.meeting_role === "meeting-host" && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={openPresentationManager} startIcon={Icons.default.FilePresent}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">{MeetingStore.getMeetingState.presentingVirtualBoard ? "Manage Presentations" : "Present document"}</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Present a pdf, word document or PPT file and with annotations.</Typography>
                                </Box>
                            </Button>}
                            {MeetingStore.getMeetingState?.activePresenter?.user_id === `${MeetingStore.getLocalUser.user_id}-sharing` && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={replaceSharingTrack} startIcon={Icons.default.PresentToAll}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">Share something else</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Share a different window or tab.</Typography>
                                </Box>
                            </Button>}
                            {MeetingStore.getMeetingState?.activePresenter?.user_id !== `${MeetingStore.getLocalUser.user_id}-sharing` && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={resumePresenting} startIcon={Icons.default.PresentToAll}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">Resume presenting</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Take over from active presenter.</Typography>
                                </Box>
                            </Button>}
                            <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={toggleScreenShare} startIcon={Icons.default.CancelPresentation}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">Stop presenting</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Stop your current screen sharing.</Typography>
                                </Box>
                            </Button>
                        </MenuList>}
                        {!MeetingStore.getMeetingState.isSharing && <Box maxWidth="350px">
                            {MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={startSharingScreen} startIcon={Icons.default.PresentToAll}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">Present your screen</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Present your system's screen or browser tab or window.</Typography>
                                </Box>
                            </Button>}
                            {MeetingStore.getLocalUser.meeting_role === "meeting-host" && MeetingStore.getCurrentMeeting.meeting_type !== "discussion" && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={openPresentationManager} startIcon={Icons.default.FilePresent}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">{MeetingStore.getMeetingState.presentingVirtualBoard ? "Manage Presentations" : "Present document"}</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Present a pdf, word document or PPT file and with annotations.</Typography>
                                </Box>
                            </Button>}
                            {MeetingStore.getMeetingState?.activePresenter?.user_id === `${MeetingStore.getLocalUser.user_id}` && <Button fullWidth sx={{ gap: "20px", display: "flex", justifyContent: "flex-start", padding: "10px 20px" }} onClick={stopSharingVirtualBoard} startIcon={Icons.default.CancelPresentation}>
                                <Box display="flex" flexDirection="column" alignItems="flex-start"  >
                                    <Typography textTransform="initial" textAlign="left" variant='subtitle2' display="block" fontWeight="500">Stop presenting</Typography>
                                    <Typography textAlign="left" variant='subtitle2' display="block" color='textSecondary' lineHeight="1.5" textTransform="initial" fontSize="12px" fontWeight="400">Stop sharing virtualboard.</Typography>
                                </Box>
                            </Button>}
                        </Box>}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* MEETING ACTIONS */}
            <Box display="flex" gridArea="pa" justifyContent={{ md: "center", xs: "flex-start" }} gap="10px">
                <Box ref={videoActionBtnGrpRef} bgcolor={palette.rowHover} borderRadius="20px" padding={{ sm: "2px", xs: "0" }} flexShrink={0} display="flex" alignItems="center" gap={{ xs: "0", md: "10px" }}>

                    <MinimizedVideoSettingsBar openSettings={openVideoSettings} setOpen={setShowMinimizedVideoSettings} open={showMinimizedVideoSettings} anchorEl={videoActionBtnGrpRef} />

                    <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title={cameraButtonDetails.tooltip}>
                        <Badge color={cameraButtonDetails.badgeColor} overlap='circular' invisible={cameraButtonDetails.hideBadge} badgeContent={cameraButtonDetails.badgeContent} >
                            <IconButton size={cameraButtonDetails.size} disabled={cameraButtonDetails.disabled} sx={{ ...cameraButtonDetails.sx }} onClick={publishVideo} >
                                {cameraButtonDetails.icon}
                            </IconButton>
                        </Badge>
                    </Tooltip>
                    <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} sx={{ marginLeft: "-3px" }} disableInteractive title="Video Settings">
                        <IconButton sx={{ display: { xs: "flex", sm: "flex" } }} onClick={() => setShowMinimizedVideoSettings( true )} size='small'>
                            {Icons.default.KeyboardArrowUp}
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box ref={audioActionBtnGrpRef} bgcolor={palette.rowHover} padding={{ sm: "2px", xs: "0" }} borderRadius="20px" display="flex" flexShrink={0} alignItems="center" gap={{ xs: "0", md: "10px" }}>

                    <MinimizedAudioSettingsBar openSettings={openAudioSettings} open={showMinimizedAudioSettings} setOpen={setShowMinimizedAudioSettings} anchorEl={audioActionBtnGrpRef} />

                    <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title={micButtonDetails.tooltip}>
                        <Badge badgeContent={micButtonDetails.badgeContent} color={micButtonDetails.badgeColor} overlap='circular' invisible={micButtonDetails.hideBadge} >
                            <IconButton size={micButtonDetails.size} disabled={micButtonDetails.disabled} sx={{ ...micButtonDetails.sx }} onClick={publishAudio}>
                                {micButtonDetails.icon}
                            </IconButton>
                        </Badge>
                    </Tooltip>
                    <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} sx={{ marginLeft: "-3px" }} disableInteractive title="Audio Settings">
                        <IconButton sx={{ display: { xs: "flex", sm: "flex" } }} onClick={() => setShowMinimizedAudioSettings( true )} size='small'>
                            {Icons.default.KeyboardArrowUp}
                        </IconButton>
                    </Tooltip>
                </Box>
                {!isMobileDevice().isMobile && ( ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" && MeetingStore.getLocalUser.meeting_role === "meeting-host" ) || ( MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" ) ) && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} placement='top' disableInteractive title={shareButtonDetails.tooltip}>
                    <Badge badgeContent={shareButtonDetails.badgeContent} color={shareButtonDetails.badgeColor} overlap='circular' invisible={shareButtonDetails.hideBadge} >
                        <IconButton size={smallWidth ? "small" : "medium"} ref={toggleShareButtonRef} disabled={shareButtonDetails.disabled} sx={shareButtonDetails.sx} onClick={handleToggleScreeShareButtonClick}  >
                            {shareButtonDetails.icon}
                        </IconButton>
                    </Badge>
                </Tooltip>}

                {!tinyWidth && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && MeetingStore.getCurrentMeetingSettings.enable_reactions && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title="Reactions">
                    <IconButton sx={{ fontSize: "24px", bgcolor: palette.rowHover, borderRadius: "20px", paddingX: { xs: "10px", sm: "15px" } }} size={smallWidth ? "small" : "medium"} onClick={event => setReactionBarAnchorlEl( event.currentTarget )}>
                        {Icons.default.EmojiEmotions}
                    </IconButton>
                </Tooltip>}

                {!tinyWidth && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title={raiseHandButtonDetails.tooltip}>
                    <IconButton size={smallWidth ? "small" : "medium"} sx={raiseHandButtonDetails.sx} onClick={raiseHand}>
                        {raiseHandButtonDetails.icon}
                    </IconButton>
                </Tooltip>}

                {MeetingStore.getLocalUser.meeting_role === "meeting-host" && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title="Manege polls">
                    <IconButton size={smallWidth ? "small" : "medium"} sx={{ bgcolor: palette.rowHover, paddingX: { xs: "10px", sm: "15px" }, borderRadius: "20px", display: { md: "flex", xs: "none" } }} onClick={() => { setIsManagingPoll( true ) }} >
                        <Checklist sx={{ color: palette.common.font }} />
                    </IconButton>
                </Tooltip>}

                <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title={leaving ? "Leaving..." : "Leave meeting"}>
                    <Box component="span">
                        <IconButton disabled={leaving} size={smallWidth ? "small" : "medium"} sx={{ height: "100%", bgcolor: palette.error.main, borderRadius: "20px", paddingX: { xs: "10px", sm: "15px" }, ":hover": { bgcolor: palette.error.dark }, ":disabled": { bgcolor: palette.error.light + "88" } }} onClick={handleLeaveChannel}>
                            {leaving ? <CircularProgress size={24} /> : <CallEnd sx={{ color: palette.common.white }} />}
                        </IconButton>
                    </Box>
                </Tooltip>
            </Box>

            {/* RIGHT-END ACTIONS */}
            <Box display="flex" gridArea="sa" justifyContent="flex-end" height="100%" gap="10px">

                {MeetingStore.getLocalUser?.meeting_role === "viewer" && MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && MeetingStore.getPolls.length > 0 && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title="Open last poll">
                    <IconButton size={smallWidth ? "small" : "medium"} sx={{ bgcolor: palette.rowHover, paddingX: { xs: "10px", sm: "15px" }, borderRadius: "20px", display: { md: "flex", xs: "none" } }} onClick={openMinimizedPoll} >
                        {Icons.default.Ballot}
                    </IconButton>
                </Tooltip>}

                {MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title="Chat">
                    <IconButton size={smallWidth ? "small" : "medium"} sx={{ bgcolor: palette.rowHover, paddingX: { xs: "10px", sm: "15px" }, borderRadius: "20px", display: { sm: "flex", xs: "none" } }} onClick={openChatsList} >
                        <Badge variant='dot' color='error' invisible={!MeetingStore.getMeetingChatsDetails.indicators.chatbutton} >
                            {Icons.default.ChatBubble}
                        </Badge>
                    </IconButton>
                </Tooltip>}

                {MeetingStore.getLocalUser.meeting_role === "meeting-host" && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive title="Participants settings">
                    <IconButton size={smallWidth ? "small" : "medium"} sx={{ bgcolor: palette.rowHover, paddingX: { xs: "10px", sm: "15px" }, display: { md: "flex", xs: "none" }, borderRadius: "20px" }} onClick={openParticipantsSettings} >
                        {Icons.default.ManageAccounts}
                    </IconButton>
                </Tooltip>}


                {/* MORE ACTIONS */}
                <Box height="100%" display="flex" gap="10px">

                    <Popover sx={{ zIndex: 6000 }} container={actionBarRef.current} slotProps={{ paper: { elevation: 1, sx: { borderRadius: "10px", minWidth: "250px", marginTop: "-10px", bgcolor: palette.contentBg } } }} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "right" }} onClick={() => setShowMoreOptions( null )} anchorEl={showMoreOptions} open={Boolean( showMoreOptions )}>
                        <MenuList >

                            {MeetingStore.getLocalUser.user_id === MeetingStore.getCurrentMeeting.created_by &&
                                <MenuItem sx={{ margin: 0, borderRadius: "0", minWidth: 0, padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} onClick={toggleScreenRecording} >
                                    <ListItemIcon>
                                        {MeetingStore.getMeetingState.startingOrStopingRecording ? <CircularProgress size={20} /> : recordButtonDetails.endIcon}
                                    </ListItemIcon>
                                    <ListItemText sx={{ minWidth: 0 }}>
                                        <Typography variant='subtitle2' display="block">Record session</Typography>
                                        <Typography color='textSecondary' fontSize="12px" maxWidth="100px" display="block" component="span" >Record this online session to <br /> watch and review it later.</Typography>
                                    </ListItemText>
                                </MenuItem>}
                            {MeetingStore.getLocalUser.user_id === MeetingStore.getCurrentMeeting.created_by && <Divider />}

                            {/* Settings */}
                            <MenuItem onClick={() => setShowSettingsDialog( "general" )} sx={{ margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.Settings}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2' >Settings</Typography></ListItemText>
                            </MenuItem>


                            {/* Participants settings */}
                            {MeetingStore.getLocalUser.meeting_role === "meeting-host" && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <MenuItem onClick={openParticipantsSettings} sx={{ display: { md: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.ManageAccounts}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2' >Participants settings</Typography></ListItemText>
                            </MenuItem>}

                            {/* Background Effects */}
                            <MenuItem onClick={openBackgroundSettings} sx={{ margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.AutoFixHigh}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2' >Video Backgrounds</Typography></ListItemText>
                            </MenuItem>

                            {/* Fullscreen */}
                            <MenuItem onClick={() => MeetingStore.toggleFullScreen( meetingContainer.current )} sx={{ margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {MeetingStore.getMeetingRoomLayoutSettings.isInFullscreen ? <FullscreenExit sx={{ fontSize: "inherit", color: palette.common.font }} /> : <Fullscreen sx={{ fontSize: "inherit", color: palette.common.font }} />}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2' >{MeetingStore.getMeetingRoomLayoutSettings.isInFullscreen ? "Exit fullscreen" : "View in fullscreen"}</Typography></ListItemText>
                            </MenuItem>

                            {/* Chats */}
                            {MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <MenuItem sx={{ display: { sm: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={openChatsList} >
                                <Badge variant='dot' color='error' invisible={!MeetingStore.getMeetingChatsDetails.indicators.chatbutton} >
                                    <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                        {Icons.default.ChatBubble}
                                    </ListItemIcon>
                                    <ListItemText><Typography variant='subtitle2'>Chats</Typography></ListItemText>
                                </Badge>
                            </MenuItem>}

                            {/* Polls */}
                            {MeetingStore.getLocalUser?.meeting_role === "meeting-host" && <MenuItem sx={{ display: { md: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={() => { setIsManagingPoll( true ) }} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.Checklist}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2'>Manage Polls</Typography></ListItemText>
                            </MenuItem>}

                            {/* Minimized poll voter */}
                            {MeetingStore.getLocalUser?.meeting_role === "viewer" && MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && MeetingStore.getPolls.length > 0 && <MenuItem sx={{ display: { md: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={openMinimizedPoll} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.Ballot}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2'>Answer Polls</Typography></ListItemText>
                            </MenuItem>}

                            {tinyWidth && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && MeetingStore.getCurrentMeetingSettings.enable_reactions && <MenuItem sx={{ display: { xs: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={event => setReactionBarAnchorlEl( actionBarRef.current )} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.EmojiEmotions}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2'>Reactions</Typography></ListItemText>
                            </MenuItem>}
                            {tinyWidth && MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" && <MenuItem sx={{ ...raiseHandButtonDetails.sx, display: { sm: "none", xs: "flex" }, margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={raiseHand} >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {raiseHandButtonDetails.icon}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2'>{raiseHandButtonDetails.label}</Typography></ListItemText>
                            </MenuItem>}

                            {/* Session Info */}
                            {MeetingStore.getLocalUser?.meeting_role === "meeting-host" && <MenuItem sx={{ margin: 0, borderRadius: "0", padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} onClick={openMeetingInfoDialog}   >
                                <ListItemIcon sx={{ fontSize: "24px", color: "inherit" }} >
                                    {Icons.default.Info}
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2' >View Session Info</Typography></ListItemText>
                            </MenuItem>}

                        </MenuList>
                    </Popover>


                    <Tooltip slotProps={{ popper: { container: meetingContainer.current } }} disableInteractive placement='top-end' title="More options">
                        <IconButton size={smallWidth ? "small" : "medium"} sx={{ bgcolor: palette.rowHover, paddingX: "5px", borderRadius: "20px" }} onClick={( e ) => setShowMoreOptions( e.currentTarget )} className={MeetingStore.getMeetingState.isVideoPublished ? "IconButton-on" : ""}>
                            <MoreVert sx={{ color: palette.common.font }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

        </Box >
    )
} )

export default ActionsBar


// {
//     showModal && <Dialog open={true}>
//         <Box maxWidth="350px" padding="20px">
//             <Typography variant="h6" component="h2" display="block" >You've been inactive</Typography>
//             <Typography marginTop="20px" fontSize="14px">We noticed no activity for a while. To keep the meeting active, please confirm you’re still here.</Typography>
//             <Box marginTop="20px" display="flex" gap="10px" alignItems="center">
//                 <Box width="fit-content" position="relative">
//                     <CircularProgress sx={{ color: palette.grey[500], position: "relative" }} variant='determinate' value={100} />
//                     <CircularProgress sx={{ position: "absolute", inset: 0 }} variant='determinate' value={( ( graceLeft / 1000 ) / 60 ) * 100} />
//                     <Typography marginTop="-5px" fontSize="12px" display="flex" alignItems="center" justifyContent="center" position="absolute" sx={{ inset: 0 }} >{graceLeft / 1000}s</Typography>
//                 </Box>
//                 <Typography marginTop="-5px" fontSize="12px" color='GrayText'>Disconnecting in {graceLeft / 1000} seconds....</Typography>
//             </Box>
//             <Box marginTop="20px" display="flex" alignItems="center" gap="10px">
//                 <Button onClick={resetTimers}>Stay in</Button>
//                 <Button onClick={leaveChannel} color='error'>Leave Meeting</Button>
//             </Box>
//         </Box>
//     </Dialog>
// }
/* < MenuItem sx={{ display: { sm: "none", xs: "flex" }, padding: "10px", justifyContent: "flex-start", ":hover": { bgcolor: palette.action.hover } }} size='small' onClick={openParticipantsList} >
                                <ListItemIcon>
                                    <Group sx={{ color: palette.common.font }} />
                                </ListItemIcon>
                                <ListItemText><Typography variant='subtitle2'>View participants</Typography></ListItemText>
                            </MenuItem> 
                            
                            
    const MOVING_ACTION_BUTTONS = useMemo( () => {

        const getDefaultStyles = ( display = true ) => {
            const styles = {}
            if ( display )
                styles.display = { md: "flex", xs: "none" }
            styles.paddingX = { xs: "10px", sm: "15px" }
            styles.borderRadius = "20px"
            styles.bgcolor = palette.rowHover

            return styles
        }

        const hybridClass = MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class"
        const meetingHost = MeetingStore.getLocalUser.meeting_role === "meeting-host"

        return [
            { id: "reactions", style: getDefaultStyles( false ), icon: Icons.default.EmojiEmotions, label: "Reactions", tooltip: "Reactions", render: { center: !tinyWidth && !hybridClass && MeetingStore.getCurrentMeetingSettings.enable_reactions, more: tinyWidth && !hybridClass && MeetingStore.getCurrentMeetingSettings.enable_reactions }, action: event => setReactionBarAnchorlEl( event.currentTarget ) },
            { id: "raise-hand", style: getDefaultStyles(), icon: raiseHandButtonDetails.icon, label: raiseHandButtonDetails.label, tooltip: raiseHandButtonDetails.tooltip, render: { center: !tinyWidth && !hybridClass, more: tinyWidth && !hybridClass }, action: raiseHand },
            { id: "viewer-poll", style: getDefaultStyles(), icon: Icons.default.Ballot, label: "Open latest polls", tooltip: "Open latest polls", render: { center: !meetingHost && MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && MeetingStore.getPolls.length > 0, more: MeetingStore.getLocalUser?.meeting_role === "viewer" && MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && MeetingStore.getPolls.length > 0 }, action: openMinimizedPoll },
            { id: "host-poll", style: getDefaultStyles(), icon: Icons.default.Checklist, label: "Manage polls", tooltip: "Manage polls", render: { center: meetingHost, more: meetingHost }, action: () => { setIsManagingPoll( true ) } },
            { id: "chat", badge: "chatbutton", style: getDefaultStyles(), icon: Icons.default.Checklist, label: "Manage polls", tooltip: "Manage polls", render: { center: !hybridClass, more: !hybridClass }, action: openChatsList },
        ]
    }, [tinyWidth, smallWidth, raiseHandButtonDetails] )
                            
                            */